import { c as _c } from "react/compiler-runtime";
import { ReactNode, useEffect, useState } from 'react';
import { Box, Button, Flex, Spinner, Text } from '@chakra-ui/react';
import Link, { useLinkStatus } from 'next/link';
import { LuChevronDown, LuChevronRight } from 'react-icons/lu';
import { useNavigationProgress } from './navigationProgressContext';
import { sidebarRowStyles } from './sidebarItemStyles';
import { SIDEBAR_ITEMS, type SidebarItem } from './sidebarNavItems';
import { Tooltip } from '../ui/tooltip';
type IconComponent = SidebarItem['icon'];

// Lives inside <Link>; useLinkStatus only reports pending for the
// nearest ancestor Link. Swaps the route icon for a spinner while the
// click is in flight, and increments the shared navigation-progress
// counter so the top bar appears.
const NavItemIndicator = t0 => {
  const $ = _c(8);
  const {
    icon: Icon
  } = t0;
  const {
    pending
  } = useLinkStatus();
  const {
    begin,
    end
  } = useNavigationProgress();
  let t1;
  let t2;
  if ($[0] !== begin || $[1] !== end || $[2] !== pending) {
    t1 = () => {
      if (!pending) {
        return;
      }
      begin();
      return () => end();
    };
    t2 = [pending, begin, end];
    $[0] = begin;
    $[1] = end;
    $[2] = pending;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  if (pending) {
    let t3;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <Spinner color="inherit" size="sm" />;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  let t3;
  if ($[6] !== Icon) {
    t3 = <Icon size={18} />;
    $[6] = Icon;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
const EXPANDED_WIDTH = '240px';
const COLLAPSED_WIDTH = '56px';
type SidebarProps = {
  collapsed: boolean;
  collaborationEnabled: boolean;
  pathname: string;
  networkId: string | undefined;
  viewId: string | undefined;
  /**
   * Rendered after Network browser's children when a network is open.
   * The container (e.g. AppShell) provides this so Sidebar itself stays
   * free of tRPC-backed dependencies that would break in Storybook.
   */
  extraNetworkBrowserItem?: ReactNode;
};
type ItemProps = {
  item: Pick<SidebarItem, 'key' | 'label' | 'ariaLabel' | 'icon' | 'hrefFor' | 'matchPath'>;
  collapsed: boolean;
  pathname: string;
  networkId: string | undefined;
  viewId: string | undefined;
  indent: boolean;
  /**
   * True when the item itself isn't active but one of its sub-route children
   * is. Used to give the parent row a subtle "section active" accent.
   */
  sectionActive?: boolean;
};
const NavItem = t0 => {
  const $ = _c(41);
  const {
    item,
    collapsed,
    pathname,
    networkId,
    viewId,
    indent,
    sectionActive: t1
  } = t0;
  const sectionActive = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== item || $[1] !== networkId || $[2] !== pathname || $[3] !== viewId) {
    t2 = item.matchPath(pathname, {
      networkId,
      viewId
    });
    $[0] = item;
    $[1] = networkId;
    $[2] = pathname;
    $[3] = viewId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const isActive = t2;
  let t3;
  if ($[5] !== item || $[6] !== networkId || $[7] !== viewId) {
    t3 = item.hrefFor({
      networkId,
      viewId
    });
    $[5] = item;
    $[6] = networkId;
    $[7] = viewId;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const href = t3;
  const showSectionAccent = sectionActive && !isActive;
  const t4 = item.label;
  const t5 = !collapsed;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      placement: "right"
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = isActive ? "page" : undefined;
  const t8 = item.ariaLabel;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      display: "block",
      width: "100%"
    };
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const t10 = showSectionAccent ? "blue.400" : "transparent";
  const t11 = isActive ? "blue" : "gray";
  const t12 = isActive || undefined;
  const t13 = showSectionAccent || undefined;
  const t14 = `sidebar-nav-${item.key}`;
  let t15;
  if ($[11] !== collapsed || $[12] !== indent) {
    t15 = sidebarRowStyles(collapsed, indent);
    $[11] = collapsed;
    $[12] = indent;
    $[13] = t15;
  } else {
    t15 = $[13];
  }
  const t16 = isActive ? "solid" : "ghost";
  const t17 = collapsed ? "center" : "flex-start";
  let t18;
  if ($[14] !== item.icon) {
    t18 = <NavItemIndicator icon={item.icon} />;
    $[14] = item.icon;
    $[15] = t18;
  } else {
    t18 = $[15];
  }
  let t19;
  if ($[16] !== collapsed || $[17] !== item.label) {
    t19 = !collapsed && <Text fontSize="sm" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{item.label}</Text>;
    $[16] = collapsed;
    $[17] = item.label;
    $[18] = t19;
  } else {
    t19 = $[18];
  }
  let t20;
  if ($[19] !== t17 || $[20] !== t18 || $[21] !== t19) {
    t20 = <Flex alignItems="center" gap={3} justifyContent={t17} w="100%">{t18}{t19}</Flex>;
    $[19] = t17;
    $[20] = t18;
    $[21] = t19;
    $[22] = t20;
  } else {
    t20 = $[22];
  }
  let t21;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t12 || $[26] !== t13 || $[27] !== t14 || $[28] !== t15 || $[29] !== t16 || $[30] !== t20) {
    t21 = <Button borderLeftColor={t10} borderLeftWidth="2px" colorPalette={t11} data-active={t12} data-section-active={t13} data-testid={t14} {...t15} variant={t16}>{t20}</Button>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
    $[29] = t16;
    $[30] = t20;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  let t22;
  if ($[32] !== href || $[33] !== item.ariaLabel || $[34] !== t21 || $[35] !== t7) {
    t22 = <Box w="100%"><Link aria-current={t7} aria-label={t8} href={href} style={t9}>{t21}</Link></Box>;
    $[32] = href;
    $[33] = item.ariaLabel;
    $[34] = t21;
    $[35] = t7;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== item.label || $[38] !== t22 || $[39] !== t5) {
    t23 = <Tooltip content={t4} disabled={t5} openDelay={200} positioning={t6}>{t22}</Tooltip>;
    $[37] = item.label;
    $[38] = t22;
    $[39] = t5;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  return t23;
};
type GroupToggleProps = {
  item: Pick<SidebarItem, 'label' | 'ariaLabel' | 'icon' | 'key'>;
  collapsed: boolean;
  expanded: boolean;
  sectionActive: boolean;
  onToggle: () => void;
};
const GroupToggleRow = t0 => {
  const $ = _c(26);
  const {
    item,
    collapsed,
    expanded,
    sectionActive,
    onToggle
  } = t0;
  const {
    icon: Icon
  } = item;
  const Chevron = expanded ? LuChevronDown : LuChevronRight;
  const t1 = item.label;
  const t2 = !collapsed;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      placement: "right"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const t4 = item.ariaLabel;
  const t5 = sectionActive ? "blue.400" : "transparent";
  const t6 = sectionActive || undefined;
  const t7 = `sidebar-nav-${item.key}`;
  let t8;
  if ($[1] !== collapsed) {
    t8 = sidebarRowStyles(collapsed, false);
    $[1] = collapsed;
    $[2] = t8;
  } else {
    t8 = $[2];
  }
  const t9 = collapsed ? "center" : "flex-start";
  let t10;
  if ($[3] !== Icon) {
    t10 = <Icon size={18} />;
    $[3] = Icon;
    $[4] = t10;
  } else {
    t10 = $[4];
  }
  let t11;
  if ($[5] !== Chevron || $[6] !== collapsed || $[7] !== item.label) {
    t11 = !collapsed && <><Text fontSize="sm" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">{item.label}</Text><Box flex="1" /><Chevron size={16} /></>;
    $[5] = Chevron;
    $[6] = collapsed;
    $[7] = item.label;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  let t12;
  if ($[9] !== t10 || $[10] !== t11 || $[11] !== t9) {
    t12 = <Flex alignItems="center" gap={3} justifyContent={t9} w="100%">{t10}{t11}</Flex>;
    $[9] = t10;
    $[10] = t11;
    $[11] = t9;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== expanded || $[14] !== item.ariaLabel || $[15] !== onToggle || $[16] !== t12 || $[17] !== t5 || $[18] !== t6 || $[19] !== t7 || $[20] !== t8) {
    t13 = <Button aria-expanded={expanded} aria-label={t4} borderLeftColor={t5} borderLeftWidth="2px" colorPalette="gray" data-section-active={t6} data-testid={t7} {...t8} onClick={onToggle}>{t12}</Button>;
    $[13] = expanded;
    $[14] = item.ariaLabel;
    $[15] = onToggle;
    $[16] = t12;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== item.label || $[23] !== t13 || $[24] !== t2) {
    t14 = <Tooltip content={t1} disabled={t2} openDelay={200} positioning={t3}>{t13}</Tooltip>;
    $[22] = item.label;
    $[23] = t13;
    $[24] = t2;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  return t14;
};
const Sidebar = t0 => {
  const $ = _c(19);
  const {
    collapsed,
    collaborationEnabled,
    pathname,
    networkId,
    viewId,
    extraNetworkBrowserItem
  } = t0;
  const showNetworkChildren = !!networkId;
  let t1;
  if ($[0] !== networkId || $[1] !== pathname || $[2] !== viewId) {
    t1 = () => new Set(SIDEBAR_ITEMS.filter(item => item.collapsibleGroup && item.children?.some(child => child.matchPath(pathname, {
      networkId,
      viewId
    }))).map(_temp));
    $[0] = networkId;
    $[1] = pathname;
    $[2] = viewId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [expandedGroups, setExpandedGroups] = useState(t1);
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = key => setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const toggleGroup = t2;
  const handleNavKeyDown = _temp2;
  const t3 = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;
  let t4;
  if ($[5] !== collaborationEnabled || $[6] !== collapsed || $[7] !== expandedGroups || $[8] !== extraNetworkBrowserItem || $[9] !== networkId || $[10] !== pathname || $[11] !== showNetworkChildren || $[12] !== viewId) {
    t4 = SIDEBAR_ITEMS.map(item_1 => {
      if (item_1.key === "collaboration" && !collaborationEnabled) {
        return null;
      }
      const isNetworkBrowser = item_1.key === "network-browser";
      const sectionActive = !!item_1.children?.some(child_0 => child_0.matchPath(pathname, {
        networkId,
        viewId
      }));
      const renderChildren = item_1.collapsibleGroup ? expandedGroups.has(item_1.key) : item_1.children && showNetworkChildren;
      return <Flex key={item_1.key} direction="column" gap={1}>{item_1.collapsibleGroup ? <GroupToggleRow collapsed={collapsed} expanded={expandedGroups.has(item_1.key)} item={item_1} sectionActive={sectionActive} onToggle={() => toggleGroup(item_1.key)} /> : <NavItem collapsed={collapsed} indent={false} item={item_1} networkId={networkId} pathname={pathname} sectionActive={sectionActive} viewId={viewId} />}{renderChildren && item_1.children?.map(child_1 => <NavItem key={child_1.key} collapsed={collapsed} indent={!collapsed} item={child_1} networkId={networkId} pathname={pathname} viewId={viewId} />)}{isNetworkBrowser && showNetworkChildren && extraNetworkBrowserItem}</Flex>;
    });
    $[5] = collaborationEnabled;
    $[6] = collapsed;
    $[7] = expandedGroups;
    $[8] = extraNetworkBrowserItem;
    $[9] = networkId;
    $[10] = pathname;
    $[11] = showNetworkChildren;
    $[12] = viewId;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t4) {
    t5 = <Flex direction="column" gap={1} p={2}>{t4}</Flex>;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t3 || $[17] !== t5) {
    t6 = <Box aria-label="Main navigation" as="nav" bg="gray.50" borderRightColor="gray.200" borderRightWidth="1px" flexShrink={0} height="100%" overflow="hidden" transition="width 0.15s ease" width={t3} onKeyDown={handleNavKeyDown}>{t5}</Box>;
    $[16] = t3;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
};
export default Sidebar;
function _temp(item_0) {
  return item_0.key;
}
function _temp2(event) {
  if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
    return;
  }
  const nav = event.currentTarget;
  const items = Array.from(nav.querySelectorAll("a[aria-label^=\"select-\"], button[aria-label^=\"select-\"], a[aria-label=\"edit-variable-types-button\"], button[aria-label=\"optimize-button\"]"));
  if (items.length === 0) {
    return;
  }
  const active = document.activeElement as HTMLElement | null;
  const currentIndex = active ? items.indexOf(active) : -1;
  let nextIndex;
  if (currentIndex === -1) {
    nextIndex = event.key === "ArrowDown" ? 0 : items.length - 1;
  } else {
    if (event.key === "ArrowDown") {
      nextIndex = Math.min(currentIndex + 1, items.length - 1);
    } else {
      nextIndex = Math.max(currentIndex - 1, 0);
    }
  }
  items[nextIndex]?.focus();
  event.preventDefault();
}